import { db } from "../firebaseClient";
import { collection, doc, getDoc, query, where, getDocs } from "firebase/firestore";
import { mergeDocument } from "utility/firestoreManager";

const collectionName = "configs";
const configsCollection = collection(db, collectionName);

const ROBOT_DOC_ID = "robot::sigil::sigil";

const getRobotSubmissionAccounts = async () => {
  const snap = await getDoc(doc(db, collectionName, ROBOT_DOC_ID));
  if (!snap.exists()) return {};
  return snap.data()?.props?.marketplace?.amazon?.domainMapping ?? {};
};

const configTypes = {
  ANNOUNCEMENT: "announcement",
  BRAND: "brand",
};

const DEFAULT_TENANT = "sigil";

const setMapTemplate = async (auth, template, globalConfig) => {
  const { brand } = globalConfig;
  const { ref } = brand;

  let current = brand.props?.map?.templates?.filter(
    (t) => t.id !== template.id,
  );
  let update = null;

  if (!brand.props?.map?.templates) {
    update = [template];
  } else {
    update = [...current, template];
  }

  await mergeDocument(auth, ref, {
    props: {
      map: {
        templates: update,
      },
    },
  });
};

const deleteMapTemplate = async (auth, templateId, globalConfig) => {
  const { brand } = globalConfig;
  const { ref } = brand;

  const update = brand.props?.map?.templates?.filter(
    (c) => c.id !== templateId,
  );
  await mergeDocument(auth, ref, {
    props: {
      map: {
        templates: update,
      },
    },
  });
};

const updateTenantConfig = async (auth, globalConfig, update) => {
  const { tenant } = globalConfig;
  const { ref } = tenant;

  await mergeDocument(auth, ref, update);
};

const updateUserConfig = async (auth, globalConfig, update) => {
  const { user } = globalConfig;
  const { ref } = user;

  await mergeDocument(auth, ref, update);
};

const updateBrandConfigMarketSearches = async (
  auth,
  globalConfig,
  brandName,
  newSearchEntry,
) => {
  const brandDoc = await getBrandByName(globalConfig, brandName);

  const { ref } = brandDoc;
  const data = brandDoc.data();

  const currentMarketSearches = data?.marketSearches || [];
  const updatedMarketSearches = [...currentMarketSearches, newSearchEntry];

  await mergeDocument(auth, ref, {
    marketSearches: updatedMarketSearches,
  });
};

const getAnnouncements = async () => {
  const announcementQuery = query(
    configsCollection,
    where("tenant", "==", DEFAULT_TENANT),
    where("type", "==", configTypes.ANNOUNCEMENT),
  );
  const snapShot = await getDocs(announcementQuery);

  return snapShot.docs
    .map((doc) => doc.data())
    .sort((a, b) => {
      const dateA = a.createdAtUtc?.toDate() || new Date(0);
      const dateB = b.createdAtUtc?.toDate() || new Date(0);
      return dateB - dateA;
    });
};

const getBrands = async (globalConfig) => {
  const { tenant } = globalConfig;

  const brandQuery = query(
    configsCollection,
    where("tenant", "==", tenant.tenant),
    where("type", "==", configTypes.BRAND),
  );
  const snapShot = await getDocs(brandQuery);

  return snapShot.docs
    .map((doc) => doc.data())
    .sort((a, b) => {
      return a.brand.localeCompare(b.brand, undefined, { sensitivity: "base" });
    });
};

const getBrandByName = async (globalConfig, brandName) => {
  const { tenant } = globalConfig;

  const brandQuery = query(
    configsCollection,
    where("tenant", "==", tenant.tenant),
    where("type", "==", configTypes.BRAND),
    where("brand", "==", brandName),
  );
  const snapShot = await getDocs(brandQuery);

  if (snapShot.empty) {
    return null;
  }

  return snapShot.docs[0];
};

const getSigilTenant = async () => {
  const tenantQuery = query(
    configsCollection,
    where("tenant", "==", DEFAULT_TENANT),
    where("type", "==", "superTenant"),
  );
  const snapShot = await getDocs(tenantQuery);

  if (snapShot.empty) {
    return null;
  }

  return snapShot.docs[0].data();
};

const onDismissAnnouncement = async (auth, user, announcement) => {
  const { key } = announcement;
  const disabledAnnouncements = [...(user.props.disabledAnnouncements || [])];

  if (!disabledAnnouncements.includes(key)) {
    disabledAnnouncements.push(key);

    await mergeDocument(auth, user.ref, {
      props: {
        disabledAnnouncements,
      },
    });
  }
};

const getTenantUsers = async (globalConfig) => {
  const { tenant } = globalConfig;

  const userQuery = query(
    configsCollection,
    where("tenant", "==", tenant.tenant),
    where("type", "==", "user")
  );
  const snapShot = await getDocs(userQuery);

  return snapShot.docs.map((doc) => ({
    ...doc.data(),
    ref: doc.ref,
  }));
};

const updateUserEmailNotifications = async (auth, userRef, emailNotifications) => {
  await mergeDocument(auth, userRef, {
    props: {
      emailNotifications,
    },
  });
};

export {
  collectionName,
  ROBOT_DOC_ID,
  getRobotSubmissionAccounts,
  getSigilTenant,
  setMapTemplate,
  deleteMapTemplate,
  updateTenantConfig,
  updateUserConfig,
  getAnnouncements,
  onDismissAnnouncement,
  getBrands,
  updateBrandConfigMarketSearches,
  getTenantUsers,
  updateUserEmailNotifications,
};
